import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProjectService } from '../services/ProjectService';
import { Loader2, Sparkles, Dices, Play, Download, Edit3, Check, X, HelpCircle, Timer, RotateCcw, Save, Mic, Volume2, Square, Youtube } from 'lucide-react';
import { YouTubeUploadModal } from '../components/YouTubeUploadModal';
import './QuizCreatorPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Question count options
const QUESTION_COUNTS = [5, 10, 15, 20];

// Timer duration options
const TIMER_DURATIONS = [
    { value: 3, label: '3 seconds' },
    { value: 5, label: '5 seconds' },
    { value: 7, label: '7 seconds' }
];

// Voice options for narration
const VOICES = [
    { id: 'adam', label: 'Adam', description: 'Deep, authoritative male voice' },
    { id: 'brian', label: 'Brian', description: 'Deep male voice, great for narration' },
    { id: 'liam', label: 'Liam', description: 'Young male voice, natural narration' },
    { id: 'jessica', label: 'Jessica', description: 'Young, expressive female voice' },
    { id: 'matilda', label: 'Matilda', description: 'Warm female voice, audiobook style' },
    { id: 'sarah', label: 'Sarah', description: 'Soft, gentle female voice' },
];

// Voice Preview Button Component
const VoicePreviewButton = ({ voiceId, voiceList }) => {
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    const voice = voiceList.find(v => v.id === voiceId);
    const previewUrl = voice?.previewUrl;

    const togglePreview = () => {
        if (!previewUrl) return;

        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
        } else {
            if (audioRef.current) {
                audioRef.current.src = previewUrl;
                audioRef.current.play().catch(() => { });
            }
            setPlaying(true);
        }
    };

    return (
        <>
            <button
                className="voice-preview-btn"
                onClick={togglePreview}
                title={previewUrl ? "Preview voice" : "No preview available"}
                disabled={!previewUrl}
            >
                {playing ? <Square size={16} /> : <Volume2 size={16} />}
            </button>
            <audio
                ref={audioRef}
                onEnded={() => setPlaying(false)}
                style={{ display: 'none' }}
            />
        </>
    );
};


// Single Question Card Component
const QuestionCard = ({ question, index, onUpdate, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ ...question });

    const handleSave = () => {
        onUpdate(index, editData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditData({ ...question });
        setIsEditing(false);
    };

    const choiceLabels = ['A', 'B', 'C'];

    if (isEditing) {
        return (
            <div className="question-card editing">
                <div className="question-header">
                    <span className="question-number">#{index + 1}</span>
                    <div className="question-actions">
                        <button className="action-btn save" onClick={handleSave} title="Save">
                            <Check size={16} />
                        </button>
                        <button className="action-btn cancel" onClick={handleCancel} title="Cancel">
                            <X size={16} />
                        </button>
                    </div>
                </div>
                <input
                    type="text"
                    className="question-input"
                    value={editData.question}
                    onChange={(e) => setEditData({ ...editData, question: e.target.value })}
                    placeholder="Enter question..."
                />
                <div className="choices-edit">
                    {editData.choices.map((choice, i) => (
                        <div key={i} className="choice-edit-row">
                            <span className="choice-label">{choiceLabels[i]})</span>
                            <input
                                type="text"
                                value={choice}
                                onChange={(e) => {
                                    const newChoices = [...editData.choices];
                                    newChoices[i] = e.target.value;
                                    setEditData({ ...editData, choices: newChoices });
                                }}
                                placeholder={`Answer ${choiceLabels[i]}`}
                            />
                            <button
                                className={`correct-toggle ${editData.correctIndex === i ? 'active' : ''}`}
                                onClick={() => setEditData({ ...editData, correctIndex: i })}
                                title="Mark as correct"
                            >
                                <Check size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="question-card">
            <div className="question-header">
                <span className="question-number">#{index + 1}</span>
                <div className="question-actions">
                    <button className="action-btn edit" onClick={() => setIsEditing(true)} title="Edit">
                        <Edit3 size={16} />
                    </button>
                    <button className="action-btn delete" onClick={() => onDelete(index)} title="Delete">
                        <X size={16} />
                    </button>
                </div>
            </div>
            <p className="question-text">{question.question}</p>
            <div className="choices-display">
                {question.choices.map((choice, i) => (
                    <div
                        key={i}
                        className={`choice-display ${i === question.correctIndex ? 'correct' : ''}`}
                    >
                        <span className="choice-label">{choiceLabels[i]})</span>
                        <span className="choice-text">{choice}</span>
                        {i === question.correctIndex && <Check size={14} className="correct-icon" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Main Component
export const QuizCreatorPage = ({ session, refreshUsage }) => {
    // YouTube Upload Modal
    const [showYoutubeModal, setShowYoutubeModal] = useState(false);

    // Project State
    const [searchParams] = useSearchParams();
    const [projectId, setProjectId] = useState(searchParams.get('project_id'));
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved

    // Topic state
    const [topic, setTopic] = useState('');
    const [isRandomizing, setIsRandomizing] = useState(false);

    // Settings state
    const [questionCount, setQuestionCount] = useState(10);
    const [timerDuration, setTimerDuration] = useState(5);

    // Questions state
    const [questions, setQuestions] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);

    // Rendering state
    const [isRendering, setIsRendering] = useState(false);
    const [renderResult, setRenderResult] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // Backgrounds state
    const [backgrounds, setBackgrounds] = useState([]);
    const [selectedBackground, setSelectedBackground] = useState(null);

    // Music state
    const [musicList, setMusicList] = useState([]);
    const [selectedMusic, setSelectedMusic] = useState(null);
    const [playingMusic, setPlayingMusic] = useState(null); // filename of currently playing
    const [audioPlayer, setAudioPlayer] = useState(null); // Audio object

    // Error state
    const [error, setError] = useState(null);

    // Voice narration state (optional, off by default)
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState('brian');
    const [availableVoices, setAvailableVoices] = useState(VOICES);
    const [voicesLoading, setVoicesLoading] = useState(false);


    // Load project logic
    useEffect(() => {
        if (projectId && session?.access_token) {
            loadProject(projectId);
        }
        if (session?.access_token) {
            loadBackgrounds();
            loadMusic();
        }

        return () => {
            if (audioPlayer) {
                audioPlayer.pause();
            }
        };
    }, [projectId, session]);

    // Fetch available voices (including custom/cloned voices)
    useEffect(() => {
        const fetchVoices = async () => {
            if (!session?.access_token) return;
            setVoicesLoading(true);
            try {
                const res = await fetch(`${API_BASE}/api/rant/voices`, {
                    headers: { 'Authorization': `Bearer ${session.access_token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.voices) {
                        setAvailableVoices(data.voices);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch voices:", err);
            } finally {
                setVoicesLoading(false);
            }
        };
        fetchVoices();
    }, [session?.access_token]);

    const loadBackgrounds = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/quiz/backgrounds`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setBackgrounds(data.backgrounds || []);
                if (data.backgrounds?.length > 0) {
                    // Default to 1.mp4 if exists, else first one
                    if (data.backgrounds.includes('1.mp4')) {
                        setSelectedBackground('1.mp4');
                    } else {
                        setSelectedBackground(data.backgrounds[0]);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load backgrounds", err);
        }
    };

    const loadMusic = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/quiz/music`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const music = data.music || [];
                setMusicList(music);

                // Default to Countdown Abstract.mp3 if exists
                if (music.includes('Countdown Abstract.mp3')) {
                    setSelectedMusic('Countdown Abstract.mp3');
                } else if (music.length > 0) {
                    setSelectedMusic(music[0]);
                }
            }
        } catch (err) {
            console.error("Failed to load music", err);
        }
    };

    const toggleMusicPreview = (filename) => {
        if (playingMusic === filename) {
            // Stop
            audioPlayer.pause();
            setPlayingMusic(null);
        } else {
            // Play new
            if (audioPlayer) audioPlayer.pause();

            // Get signed URL just in time or use a dedicated playback endpoint?
            // Use the get_music endpoint which redirects to signed URL
            const audio = new Audio(`${API_BASE}/api/quiz/music/${filename}`);
            audio.play().catch(e => console.error("Playback failed", e));
            audio.onended = () => setPlayingMusic(null);

            setAudioPlayer(audio);
            setPlayingMusic(filename);
        }
    };


    const loadProject = async (id) => {
        try {
            const project = await ProjectService.getProject(session.access_token, id);
            if (project && project.data) {
                const data = project.data;
                if (data.topic) setTopic(data.topic);
                if (data.questions) setQuestions(data.questions);
                if (data.settings) {
                    if (data.settings.questionCount) setQuestionCount(data.settings.questionCount);
                    if (data.settings.timerDuration) setTimerDuration(data.settings.timerDuration);
                }
                if (data.background_video) setSelectedBackground(data.background_video);
                if (data.background_music) setSelectedMusic(data.background_music);

                // Restore result state if job_id exists
                if (data.renderResult) {
                    setRenderResult(data.renderResult);
                    // Try to fetch preview if job exists
                    if (data.renderResult.job_id) {
                        fetch(`${API_BASE}/api/quiz/video/${data.renderResult.job_id}`, {
                            headers: { 'Authorization': `Bearer ${session.access_token}` }
                        }).then(res => {
                            if (res.ok) return res.blob();
                            throw new Error('Video expired');
                        }).then(blob => {
                            setPreviewUrl(URL.createObjectURL(blob));
                        }).catch(() => {
                            // Video might be expired, user can re-render
                            console.log("Video preview expired or not found");
                        });
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load project:", err);
        }
    };

    const handleSaveProject = async () => {
        if (!session?.access_token) {
            alert("Please log in to save projects");
            return;
        }
        if (questions.length === 0 && !topic) {
            alert("Nothing to save!");
            return;
        }

        setSaveStatus('saving');
        try {
            const projectData = {
                topic,
                questions,
                settings: {
                    questionCount,
                    timerDuration
                },
                background_video: selectedBackground,
                background_music: selectedMusic,
                renderResult,
                // Ensure we save the job_id if we have one
                job_id: currentJobId || renderResult?.job_id,
                // Ensure we save supabase_path if we have it in renderResult
                supabase_path: renderResult?.supabase_path,
                updatedAt: new Date().toISOString()

            };

            const title = topic ? `Quiz: ${topic}` : `Untitled Quiz`;

            if (projectId) {
                await ProjectService.updateProject(session.access_token, projectId, {
                    name: title,
                    data: projectData,
                    type: 'quiz'
                });
            } else {
                const newProject = await ProjectService.createProject(
                    session.access_token,
                    'quiz',
                    title,
                    projectData
                );
                setProjectId(newProject.id);
                const newUrl = new URL(window.location);
                newUrl.searchParams.set('project_id', newProject.id);
                window.history.pushState({}, '', newUrl);
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);

        } catch (err) {
            console.error(err);
            alert("Failed to save project");
            setSaveStatus('idle');
        }
    };

    // Generate random topic
    const handleRandomTopic = async () => {
        setIsRandomizing(true);
        setError(null);
        try {
            const token = session?.access_token;
            const res = await fetch(`${API_BASE}/api/quiz/random-topic`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to generate topic');
            const data = await res.json();
            setTopic(data.topic);
        } catch (err) {
            console.error("Failed to get random topic", err);
            setError(err.message || "Failed to generate topic");
            // DO NOT FALLBACK - show error so user knows key is missing/invalid
        } finally {

            setIsRandomizing(false);
        }
    };

    // Generate questions
    const handleGenerateQuestions = async () => {
        if (!topic.trim()) return;

        setIsGenerating(true);
        setError(null);
        setQuestions([]);

        try {
            const token = session?.access_token;
            const res = await fetch(`${API_BASE}/api/quiz/generate-questions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ topic, count: questionCount })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to generate questions');

            if (data.questions) {
                setQuestions(data.questions);
                if (refreshUsage) refreshUsage();
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    // Update a question
    const handleUpdateQuestion = (index, updated) => {
        const newQuestions = [...questions];
        newQuestions[index] = updated;
        setQuestions(newQuestions);
    };

    // Delete a question
    const handleDeleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };



    // Current Job ID state to track active jobs
    const [currentJobId, setCurrentJobId] = useState(null);

    // Update currentJobId if project is loaded with one
    useEffect(() => {
        if (renderResult?.job_id) {
            setCurrentJobId(renderResult.job_id);
        }
    }, [renderResult]);

    // Download video
    const handleDownload = async () => {
        if (!renderResult?.download_url) return;

        setIsDownloading(true);
        try {
            // Fetch the video from the signed URL
            const res = await fetch(renderResult.download_url);
            if (!res.ok) throw new Error('Failed to download video');

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = renderResult.filename || `quiz_${renderResult.job_id}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsDownloading(false);
        }
    };

    // Auto-save helper
    const autoSaveProject = async (jobId, projectIdToUse) => {
        if (!session?.access_token || (!projectIdToUse && !projectId)) return;

        const idToUse = projectIdToUse || projectId;
        if (!idToUse) return;

        try {
            console.log("Auto-saving project with job_id:", jobId);

            const projectData = {
                topic,
                questions,
                settings: {
                    questionCount,
                    timerDuration
                },
                background_video: selectedBackground,
                background_music: selectedMusic,
                renderResult: null, // Don't save result yet, just the job linkage
                job_id: jobId,
                updatedAt: new Date().toISOString()
            };

            const title = topic ? `Quiz: ${topic}` : `Untitled Quiz`;

            await ProjectService.updateProject(session.access_token, idToUse, {
                name: title,
                data: projectData,
                type: 'quiz'
            });
            console.log("Auto-save successful");
        } catch (e) {
            console.error("Auto-save failed:", e);
        }
    };

    // Render video
    const handleRenderVideo = async () => {
        if (questions.length === 0) return;

        setIsRendering(true);
        setError(null);
        setRenderResult(null);
        setPreviewUrl(null);

        try {
            const token = session?.access_token;

            // Start the render job (returns immediately with job_id)
            const res = await fetch(`${API_BASE}/api/quiz/render-video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    questions,
                    settings: {
                        timer_duration: timerDuration,
                        width: 1080,
                        height: 1920
                    },
                    background_video: selectedBackground,
                    background_music: selectedMusic,
                    voice_enabled: voiceEnabled,
                    voice_id: selectedVoice
                })

            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to start render');

            const jobId = data.job_id;
            if (!jobId) throw new Error('No job ID returned');

            setCurrentJobId(jobId);

            // AUTO-SAVE: Create/Update project with job_id immediately
            if (!projectId) {
                // Determine title
                const title = topic ? `Quiz: ${topic}` : `Untitled Quiz`;
                const projectData = {
                    topic,
                    questions,
                    settings: {
                        questionCount,
                        timerDuration
                    },
                    background_video: selectedBackground,
                    background_music: selectedMusic,
                    job_id: jobId,
                    updatedAt: new Date().toISOString()
                };

                try {
                    const newProject = await ProjectService.createProject(
                        session.access_token,
                        'quiz',
                        title,
                        projectData
                    );
                    setProjectId(newProject.id);
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('project_id', newProject.id);
                    window.history.pushState({}, '', newUrl);
                    console.log("Created new project for auto-save:", newProject.id);
                } catch (createErr) {
                    console.error("Failed to create auto-save project:", createErr);
                }
            } else {
                await autoSaveProject(jobId, projectId);
            }


            // Poll for status until complete
            let attempts = 0;
            const maxAttempts = 120; // 2 minutes max

            while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second

                const statusRes = await fetch(`${API_BASE}/api/quiz/status/${jobId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const status = await statusRes.json();
                console.log(`Quiz render status: ${status.status} - ${status.message || ''}`);

                if (status.status === 'complete') {
                    // Render complete - get download URL
                    setRenderResult({
                        job_id: jobId,
                        download_url: status.download_url,
                        filename: status.filename,
                        supabase_path: status.supabase_path, // Include for storage tracking
                        question_count: status.question_count || questions.length,
                        total_duration: questions.length * (timerDuration + 2)
                    });

                    if (refreshUsage) refreshUsage();

                    // Set preview URL directly from signed URL
                    if (status.download_url) {
                        setPreviewUrl(status.download_url);
                    }

                    setIsRendering(false);
                    return;
                }

                if (status.status === 'error') {
                    throw new Error(status.message || status.error || 'Render failed');
                }

                attempts++;
            }

            throw new Error('Render timed out. Please try again.');

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsRendering(false);
        }
    };

    // Clear and start over
    const handleClear = () => {
        setQuestions([]);
        setRenderResult(null);
        setPreviewUrl(null);
        setError(null);
    };

    return (
        <div className="quiz-creator">
            <div className="quiz-header">
                <h1>Quiz Creator</h1>
                <p>Generate engaging quizzes for your Shorts in seconds</p>
                {(questions.length > 0 || renderResult) && (
                    <button
                        className="btn-secondary save-btn"
                        onClick={handleSaveProject}
                        disabled={saveStatus !== 'idle'}
                        style={{
                            margin: '12px auto 0',
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(255,255,255,0.05)',
                            color: 'white',
                            cursor: 'pointer'
                        }}
                    >
                        {saveStatus === 'saving' ? (
                            <>
                                <Loader2 size={16} className="spin" />
                                <span>Saving...</span>
                            </>
                        ) : saveStatus === 'saved' ? (
                            <>
                                <Check size={16} />
                                <span>Saved!</span>
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                <span>Save Project</span>
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Main Config Form */}
            {questions.length === 0 && !renderResult && (
                <div className="quiz-content-glass">
                    <div className="topic-section">
                        <div className="topic-input-row">
                            <input
                                type="text"
                                className="topic-input"
                                placeholder="Enter a quiz topic (e.g., 90's Hip Hop)..."
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleGenerateQuestions()}
                            />
                            <button
                                className="dice-btn"
                                onClick={handleRandomTopic}
                                disabled={isRandomizing}
                                title="Random topic"
                            >
                                <Dices size={18} className={isRandomizing ? 'spin' : ''} />
                            </button>
                        </div>

                        {error && (
                            <div className="error-message">
                                <HelpCircle size={20} />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', padding: '0 32px' }}>
                        <div className="settings-row" style={{ height: '100%', marginTop: 0 }}>
                            <div className="setting-group" style={{ width: '100%' }}>
                                <label>Questions</label>
                                <div className="option-buttons" style={{ width: '100%', justifyContent: 'space-between' }}>
                                    {QUESTION_COUNTS.map(count => (
                                        <button
                                            key={count}
                                            className={`option-btn ${questionCount === count ? 'active' : ''}`}
                                            onClick={() => setQuestionCount(count)}
                                            style={{ flex: 1, justifyContent: 'center' }}
                                        >
                                            {count}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="settings-row" style={{ height: '100%', marginTop: 0 }}>
                            <div className="setting-group" style={{ width: '100%' }}>
                                <label><Timer size={14} /> Timer</label>
                                <div className="option-buttons" style={{ width: '100%', justifyContent: 'space-between' }}>
                                    {TIMER_DURATIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            className={`option-btn ${timerDuration === opt.value ? 'active' : ''}`}
                                            onClick={() => setTimerDuration(opt.value)}
                                            style={{ flex: 1, justifyContent: 'center' }}
                                        >
                                            {opt.value}s
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Background Selection */}
                    {backgrounds.length > 0 && (
                        <div className="selection-section">
                            <h3>Select Background Video</h3>
                            <div className="selection-grid">
                                {backgrounds.map((bg, i) => (
                                    <div
                                        key={i}
                                        className={`bg-option ${selectedBackground === bg ? 'selected' : ''}`}
                                        onClick={() => setSelectedBackground(bg)}
                                        onMouseEnter={(e) => {
                                            const video = e.currentTarget.querySelector('video');
                                            if (video) video.play().catch(e => console.log('Hover play failed', e));
                                        }}
                                        onMouseLeave={(e) => {
                                            const video = e.currentTarget.querySelector('video');
                                            if (video) {
                                                video.pause();
                                                video.currentTime = 0;
                                            }
                                        }}
                                    >
                                        <video
                                            src={`${API_BASE}/api/quiz/backgrounds/${bg}`}
                                            muted
                                            loop
                                            playsInline
                                        />
                                        <div className="bg-label">{bg}</div>
                                        {selectedBackground === bg && (
                                            <div className="check-badge">
                                                <Check size={12} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Background Music Selection */}
                    {musicList.length > 0 && (
                        <div className="selection-section">
                            <h3>Select Background Music</h3>
                            <div className="selection-grid music-grid">
                                {musicList.map((music, i) => (
                                    <div
                                        key={i}
                                        className={`music-option ${selectedMusic === music ? 'selected' : ''}`}
                                        onClick={() => setSelectedMusic(music)}
                                    >
                                        <div
                                            className={`music-play-btn ${playingMusic === music ? 'playing' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleMusicPreview(music);
                                            }}
                                        >
                                            {playingMusic === music ? (
                                                <div style={{ width: '10px', height: '10px', background: 'black', borderRadius: '2px' }} />
                                            ) : (
                                                <Play size={14} fill={playingMusic === music ? "black" : "white"} />
                                            )}
                                        </div>
                                        <div className="music-info">
                                            <p className="music-name">{music}</p>
                                        </div>
                                        {selectedMusic === music && <Check size={16} color="#eeba2c" />}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Voice Narration Selection */}
                    <div className="selection-section">
                        <h3><Mic size={16} style={{ marginRight: '8px' }} />Voice Narration</h3>
                        <div className="voice-section-content">
                            <div className="voice-toggle-row">
                                <label className="voice-toggle">
                                    <input
                                        type="checkbox"
                                        checked={voiceEnabled}
                                        onChange={(e) => setVoiceEnabled(e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                    <span className="toggle-label">Enable AI Voice</span>
                                </label>
                                <span className="credit-badge">+10 credits</span>
                            </div>

                            {voiceEnabled && (
                                <div className="voice-selector-row">
                                    <div className="select-wrapper" style={{ flex: 1 }}>
                                        <select
                                            value={selectedVoice}
                                            onChange={(e) => setSelectedVoice(e.target.value)}
                                            disabled={voicesLoading}
                                        >
                                            {availableVoices.map(v => (
                                                <option key={v.id} value={v.id}>
                                                    {v.label} - {v.description}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <VoicePreviewButton voiceId={selectedVoice} voiceList={availableVoices} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ padding: '32px', display: 'flex', justifyContent: 'center' }}>

                        <button
                            className="btn-generate"
                            onClick={handleGenerateQuestions}
                            disabled={isGenerating || !topic.trim()}
                        >
                            {isGenerating ? (
                                <>
                                    <Loader2 size={18} className="spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={18} />
                                    Generate Quiz
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}


            {/* Questions List */}
            {questions.length > 0 && !renderResult && (
                <div className="quiz-content-glass">
                    <div className="questions-section">
                        <div className="questions-header">
                            <h2>Review Questions</h2>
                            <span className="question-count">{questions.length} Questions</span>
                        </div>

                        <div className="questions-list">
                            {questions.map((q, i) => (
                                <QuestionCard
                                    key={i}
                                    index={i}
                                    question={q}
                                    onUpdate={handleUpdateQuestion}
                                    onDelete={handleDeleteQuestion}
                                />
                            ))}
                        </div>

                        <div className="questions-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setQuestions([])}
                            >
                                <RotateCcw size={18} /> Start Over
                            </button>
                            <button
                                className="btn-render"
                                onClick={handleRenderVideo}
                                disabled={isRendering}
                            >
                                {isRendering ? (
                                    <>
                                        <Loader2 size={18} className="spin" /> Rendering...
                                    </>
                                ) : (
                                    <>
                                        <Play size={18} /> Creates Video
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rendering Progress / Results - Full Screen */}
            {renderResult && (
                <div className="quiz-content-glass result-screen">
                    <div className="render-complete">
                        <div className="success-icon">
                            <Check size={40} />
                        </div>
                        <h2>Video Ready!</h2>
                        <p>Your quiz video has been successfully generated.</p>

                        {previewUrl && (
                            <div className="video-preview">
                                <video
                                    src={previewUrl}
                                    controls
                                    autoPlay
                                    className="preview-player"
                                />
                            </div>
                        )}

                        <div className="render-actions">
                            {(previewUrl || renderResult.download_url) && (
                                <>
                                    <a
                                        href={previewUrl || renderResult.download_url}
                                        download={`quiz_${renderResult.job_id}.mp4`}
                                        className="btn-download"
                                    >
                                        <Download size={18} /> Download Video
                                    </a>
                                    <button
                                        className="btn-download"
                                        style={{ background: '#CC0000', borderColor: '#CC0000' }}
                                        onClick={() => setShowYoutubeModal(true)}
                                    >
                                        <Youtube size={18} /> Upload to YouTube
                                    </button>
                                </>
                            )}
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setRenderResult(null);
                                    setPreviewUrl(null);
                                    setQuestions([]);
                                    setTopic('');
                                }}
                            >
                                <RotateCcw size={16} /> Create Another
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="error-message">
                    <X size={16} />
                    {error}
                </div>
            )}

            <YouTubeUploadModal
                isOpen={showYoutubeModal}
                onClose={() => setShowYoutubeModal(false)}
                session={session}
                project={{
                    id: renderResult?.job_id ? `quiz_${renderResult.job_id}` : `quiz_${Date.now()}`,
                    name: `Quiz: ${topic}`,
                    output_url: renderResult?.download_url || previewUrl,
                    data: {
                        supabase_path: renderResult?.supabase_path,
                        script: `Quiz about ${topic}. \n\n${questions.map((q, i) => `${i + 1}. ${q.question}`).join('\n')}`
                    }
                }}
            />
        </div>
    );
};

export default QuizCreatorPage;
